import { useCallback, useEffect, useMemo, useState } from "react";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Shirt, Trash2, X, Grid3x3, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * Apparel/Textile — Style master.
 *
 * A Style is a "group of variants" — e.g. `Round-neck Tee` with sizes
 * [S,M,L,XL] and colors [Red,Blue,Green] auto-generates 12 stock_items.
 * Vouchers still reference the underlying stock_items; this page is purely
 * for managing styles + viewing the size×colour matrix.
 *
 * Two dialogs:
 *   • CreateStyleDialog — collect name + chip-style size/colour pickers + live cell count
 *   • StyleMatrixDialog — view the matrix for an existing style
 */

const emptyForm = {
  name: "", style_code: "", description: "", hsn: "", gst_rate: 5,
  base_rate: 0, unit: "Nos", group: "Primary",
  sizes: [], colors: [], season: "", brand: "",
};

// Default chips users can quick-add.
const DEFAULT_SIZE_CHIPS = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];
const DEFAULT_COLOR_CHIPS = ["White", "Black", "Red", "Blue", "Green", "Yellow", "Pink", "Grey"];


function ChipMultiSelect({ label, value, onChange, suggestions, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = (v) => {
    const t = (v || "").trim();
    if (!t) return;
    if (value.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    onChange([...value, t]);
    setDraft("");
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider text-gray-600">{label}</Label>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]" data-testid={`${testid}-chips`}>
        {value.map((v, i) => (
          <Badge key={`${v}-${i}`} variant="secondary" className="gap-1 pl-2 pr-1 py-1">
            {v}
            <button type="button" onClick={() => remove(i)} className="hover:text-red-600" aria-label={`remove-${v}`}>
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            }
          }}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
        />
        <Button type="button" size="sm" variant="outline" onClick={() => add(draft)} data-testid={`${testid}-add`}>
          Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 pt-1">
        {suggestions.filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase())).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => add(s)}
            className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
            data-testid={`${testid}-suggest-${s}`}
          >
            + {s}
          </button>
        ))}
      </div>
    </div>
  );
}


function CreateStyleDialog({ open, onOpenChange, onCreated, activeId }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) setForm(emptyForm); }, [open]);

  // Live matrix cell count = sizes × colors (with empty axes treated as 1).
  const cellCount = useMemo(() => {
    const r = Math.max(form.sizes.length, 1);
    const c = Math.max(form.colors.length, 1);
    return r * c;
  }, [form.sizes, form.colors]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Style name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/styles`, form);
      toast.success(`Style created — ${cellCount} variant${cellCount === 1 ? "" : "s"} generated`);
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not create style"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle data-testid="create-style-title">Create new style</DialogTitle>
          <DialogDescription>
            We&apos;ll auto-generate one variant stock-item per size×colour cell.
            You can add more sizes/colours later.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Style name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Round-neck Tee"
              data-testid="input-style-name"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Style code <span className="text-gray-400">(optional)</span></Label>
            <Input
              value={form.style_code}
              onChange={(e) => setForm({ ...form, style_code: e.target.value.toUpperCase() })}
              placeholder="auto-generated"
              data-testid="input-style-code"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">HSN</Label>
            <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} data-testid="input-style-hsn" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">GST %</Label>
            <Input type="number" min="0" step="0.5" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) || 0 })} data-testid="input-style-gst" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Unit</Label>
            <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="input-style-unit" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Base sale rate</Label>
            <Input type="number" min="0" step="0.01" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: Number(e.target.value) || 0 })} data-testid="input-style-base-rate" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Brand</Label>
            <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="input-style-brand" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Season</Label>
            <Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="SS24 / FW24" data-testid="input-style-season" />
          </div>

          <div className="sm:col-span-2">
            <ChipMultiSelect
              label="Sizes"
              value={form.sizes}
              onChange={(v) => setForm({ ...form, sizes: v })}
              suggestions={DEFAULT_SIZE_CHIPS}
              placeholder="Type a size and press Enter…"
              testid="sizes"
            />
          </div>
          <div className="sm:col-span-2">
            <ChipMultiSelect
              label="Colors"
              value={form.colors}
              onChange={(v) => setForm({ ...form, colors: v })}
              suggestions={DEFAULT_COLOR_CHIPS}
              placeholder="Type a colour and press Enter…"
              testid="colors"
            />
          </div>
        </div>

        <div className="border-t pt-3 flex items-center justify-between text-xs">
          <div className="text-gray-600 inline-flex items-center gap-2">
            <Grid3x3 className="w-3.5 h-3.5 text-blue-700" />
            <span>
              Will generate <strong data-testid="cell-count">{cellCount}</strong> variant{cellCount === 1 ? "" : "s"}
              {form.sizes.length + form.colors.length === 0 && " (no size/colour — single default variant)"}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel-style">Cancel</Button>
          <Button type="button" onClick={save} disabled={saving || !form.name.trim()} data-testid="btn-save-style">
            {saving ? "Creating…" : "Create style"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StyleMatrixDialog({ open, onOpenChange, sid }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !sid) { setData(null); return; }
    api.get(`/styles/${sid}/matrix`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(describeAxiosError(e, "Could not load matrix")));
  }, [open, sid]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle data-testid="matrix-title">
            {data?.style?.name || "Style"}{" "}
            <span className="font-mono text-xs text-gray-500">{data?.style?.style_code}</span>
          </DialogTitle>
          <DialogDescription>
            Closing quantity per size × colour cell. Empty cells mean no variant was created.
          </DialogDescription>
        </DialogHeader>

        {!data && <p className="text-sm text-gray-500">Loading…</p>}

        {data && data.sizes.length === 0 && data.colors.length === 0 && (
          <p className="text-sm text-gray-500" data-testid="matrix-empty">
            This style has no size/colour axes — it&apos;s a single default variant.
          </p>
        )}

        {data && (data.sizes.length > 0 || data.colors.length > 0) && (
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm border-collapse" data-testid="matrix-table">
              <thead>
                <tr>
                  <th className="border bg-gray-50 px-3 py-2 text-left text-xs uppercase text-gray-600">Colour ↓ / Size →</th>
                  {(data.sizes.length ? data.sizes : [""]).map((sz) => (
                    <th key={sz || "_"} className="border bg-gray-50 px-3 py-2 text-center text-xs uppercase text-gray-600">
                      {sz || "—"}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.colors.length ? data.colors : [""]).map((col) => (
                  <tr key={col || "_"}>
                    <td className="border px-3 py-2 font-medium text-gray-700 bg-gray-50">{col || "—"}</td>
                    {(data.sizes.length ? data.sizes : [""]).map((sz) => {
                      const cell = data.cells[col || ""]?.[sz || ""];
                      return (
                        <td key={`${col}-${sz}`} className="border px-3 py-2 text-center font-mono" data-testid={`cell-${col || "_"}-${sz || "_"}`}>
                          {cell ? (
                            <div>
                              <div className={cell.qty > 0 ? "text-emerald-700 font-semibold" : "text-gray-400"}>
                                {cell.qty.toFixed(2)}
                              </div>
                              <div className="text-[10px] text-gray-500">{formatINR(cell.rate)}</div>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-close-matrix">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Inner() {
  const { activeId } = useCompany();
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [matrixSid, setMatrixSid] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${activeId}/styles`);
      setStyles(data);
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not load styles"));
    } finally { setLoading(false); }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (s) => {
    if (!window.confirm(`Delete style "${s.name}" and all ${s.variant_count} variants?`)) return;
    try {
      await api.delete(`/styles/${s.id}`);
      toast.success("Style deleted");
      load();
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not delete style"));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Styles"
        subtitle="Apparel / Textile — each style auto-generates a grid of size × colour variant stock items."
        actions={
          <Button onClick={() => setCreateOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-new-style">
            <Plus className="w-4 h-4 mr-1.5" /> New style
          </Button>
        }
      />

      {styles.length === 0 && !loading && (
        <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center" data-testid="styles-empty">
          <Shirt className="w-10 h-10 mx-auto text-gray-400" />
          <h3 className="mt-3 font-semibold text-gray-900">No styles yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mt-1">
            A Style is a parent grouping for size + colour variants. Useful when a single design
            (like a round-neck tee) needs 10–30 SKUs across the size set.
          </p>
          <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-blue-700 hover:bg-blue-800" data-testid="btn-empty-new-style">
            Create your first style
          </Button>
        </div>
      )}

      {styles.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="styles-table">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Brand</th>
                <th className="px-3 py-2 text-center">Sizes</th>
                <th className="px-3 py-2 text-center">Colours</th>
                <th className="px-3 py-2 text-right">Variants</th>
                <th className="px-3 py-2 text-right">Total qty</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {styles.map((s) => (
                <tr key={s.id} className="border-t hover:bg-gray-50" data-testid={`row-style-${s.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700">{s.style_code}</td>
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 text-gray-600">{s.brand || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-center text-xs text-gray-600">{(s.sizes || []).join(", ") || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-center text-xs text-gray-600">{(s.colors || []).join(", ") || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.variant_count}</td>
                  <td className="px-3 py-2 text-right font-mono">{Number(s.total_opening_qty || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setMatrixSid(s.id)} data-testid={`btn-matrix-${s.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Matrix
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(s)} data-testid={`btn-delete-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateStyleDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} activeId={activeId} />
      <StyleMatrixDialog open={!!matrixSid} onOpenChange={(v) => !v && setMatrixSid(null)} sid={matrixSid} />
    </div>
  );
}

export default function Styles() {
  return <RequireCompany><Inner /></RequireCompany>;
}
